import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { useFiltersStore } from './store';
import { matches, isFilterActive } from './filter-fns';

const EMPTY_SET: ReadonlySet<string> = new Set();

export interface FilteredResult {
  matchSet: ReadonlySet<string>;
  total: number;
  matched: number;
  filterActive: boolean;
  isLoading: boolean;
}

export function useFilteredComuni(): FilteredResult {
  const applied = useFiltersStore((s) => s.applied);
  const filterActive = isFilterActive(applied);
  const { data: meta } = useQuery(queries.meta());
  // total count comes from `light` (~400 KB, always loaded) so the header
  // counter shows real numbers even before the user touches a filter.
  const { data: light } = useQuery({
    ...queries.light(meta?.version ?? ''),
    enabled: !!meta?.version,
  });
  // F1.7 lazy: don't load full comuni.json (~3.6 MB) until the user activates
  // a filter. Match-loop runs only when filterActive=true.
  const { data: comuni, isLoading } = useQuery({
    ...queries.full(meta?.version ?? ''),
    enabled: !!meta?.version && filterActive,
  });

  const computed = useMemo(() => {
    const total = light?.length ?? comuni?.length ?? 0;
    if (!filterActive) {
      return { matchSet: EMPTY_SET, total, matched: total, filterActive: false };
    }
    if (!comuni) return { matchSet: EMPTY_SET, total, matched: 0, filterActive };
    const matchSet = new Set<string>();
    for (const c of comuni) {
      if (matches(c, applied)) matchSet.add(c.istat);
    }
    return { matchSet, total: comuni.length, matched: matchSet.size, filterActive: true };
  }, [comuni, light, applied, filterActive]);

  return { ...computed, isLoading: filterActive && !comuni && isLoading };
}

export function useMatchSet(): ReadonlySet<string> {
  return useFilteredComuni().matchSet;
}
